import React, { useState, useEffect, useRef } from "react";
import { CSpinner, CBadge } from "@coreui/react-pro";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import { useNavigate, useParams } from "react-router-dom";
import {
  getPolicy,
  updatePolicy,
  uploadPolicyFiles,
} from "../../utils/backend";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { CButton } from "@coreui/react";

const EditPolicy = () => {
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [initialValues, setInitialValues] = useState({
    status: "",
    supporting_docs: [],
    cover_note_no: "",
    policy_start_date: "",
    policy_end_date: "",
  });
  const [originalValues, setOriginalValues] = useState({});
  const [policyDocs, setPolicyDocs] = useState([]);
  const [isInsuranceTypeFive, setIsInsuranceTypeFive] = useState(false);

  const fetchData = async (id) => {
    setIsLoading(true);
    try {
      const response = await getPolicy({ id: id });
      console.log(response.data);
      const fetchedValues = {
        status: response.data.status || "",
        supporting_docs: response.data.supporting_docs || [],
        cover_note_no: response.data.cover_note_no || "",
        policy_start_date: response.data.policy_start_date
          ? new Date(
              new Date(response.data.policy_start_date).getTime() -
                new Date().getTimezoneOffset() * 60000
            )
              .toISOString()
              .split("T")[0]
          : "",
        policy_end_date: response.data.policy_end_date
          ? new Date(
              new Date(response.data.policy_end_date).getTime() -
                new Date().getTimezoneOffset() * 60000
            )
              .toISOString()
              .split("T")[0]
          : "",
      };
      setInitialValues(fetchedValues);
      setOriginalValues(fetchedValues);
      setPolicyDocs(response.data.policy_docs || []);
      setIsInsuranceTypeFive(response.data.order_details.insurance_type === 5);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const validationSchema = Yup.object().shape({
    status: Yup.string()
      .required("Status is required")
      .notOneOf([""], "Please select a valid status"),
    supporting_docs: Yup.array().of(Yup.mixed()),
    cover_note_no: Yup.string(),
    policy_start_date: Yup.date(),
    policy_end_date: Yup.date(),
  });

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, setFieldTouched }
  ) => {
    setIsSubmittingForm(true);
    try {
      let supportingDocsUrls = [];

      if (
        Array.isArray(values.supporting_docs) &&
        values.supporting_docs.length > 0
      ) {
        const formData = new FormData();
        values.supporting_docs.forEach((file) => {
          formData.append("files", file);
        });

        // Upload files
        const uploadResponse = await uploadPolicyFiles(formData);
        if (
          !uploadResponse.data.files ||
          uploadResponse.data.files.length === 0
        ) {
          throw new Error("Failed to upload policy documents");
        }

        // Update supporting_docs with URLs
        supportingDocsUrls = uploadResponse.data.files.map(
          (file) => `${process.env.REACT_APP_API_URL}/uploads/${file}`
        );
      }

      const payload = {
        ...values,
        id: id,
        ...(supportingDocsUrls.length > 0
          ? { supporting_docs: supportingDocsUrls }
          : {}),
      };

      if (supportingDocsUrls.length === 0) {
        delete payload.supporting_docs;
      }

      if (isInsuranceTypeFive) {
        delete payload.cover_note_no;
        delete payload.policy_start_date;
        delete payload.policy_end_date;
      }

      const hasChanges = Object.keys(values).some((key) => {
        if (key === "supporting_docs") {
          return values[key].length !== originalValues[key].length;
        }
        return values[key] !== originalValues[key];
      });

      if (hasChanges) {
        const response = await updatePolicy(payload);
        if (response && response.data) {
          toast.success("Policy updated successfully!");
          navigate("/insurance-policies/");
        } else {
          throw new Error("Failed to update policy");
        }
      } else {
        toast.info("No changes detected.");
        navigate("/insurance-policies/");
      }
    } catch (error) {
      console.error("Failed to edit policy:", error);
      toast.error(error.message || "Failed to edit policy. Please try again.");
      setErrors({ submit: "Failed to edit policy", ...error.fieldErrors });
      Object.keys(values).forEach((field) =>
        setFieldTouched(field, true, false)
      );
    } finally {
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  const handleBackClick = () => {
    // const currentPath = window.location.pathname;
    // if (currentPath.includes(`insurance-policies/edit/${id}`)) {
    //   navigate(`/insurance-policies/details/${id}`);
    // } else {
    //   navigate("/");
    // }
    navigate("/insurance-policies");
  };

  useEffect(() => {
    fetchData(id);
  }, [id]);

  return (
    <>
      {isLoading ? (
        <div className="card border-light">
          <div className="card-body p-4">
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Skeleton
                height={40}
                width={100}
                style={{ marginRight: "10px" }}
              />
              <Skeleton height={40} width={100} />
            </div>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched, setFieldValue }) => (
            <Form>
              {isSubmittingForm && (
                <div className="form-overlay">
                  <CSpinner color="primary" />
                </div>
              )}
              <div className="card border-light">
                <div className="card-body">
                  {errors.submit && (
                    <div className="alert alert-danger">{errors.submit}</div>
                  )}
                  {/* Form fields */}
                  <div className="row">
                    {/* Status */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="status" className="form-label">
                        <span className="text-danger">*</span> Status
                      </label>{" "}
                      <div className="input-group">
                        <Field
                          name="status"
                          as="select"
                          className={`form-select ${
                            touched.status && errors.status ? "is-invalid" : ""
                          }`}
                        >
                          <option value="">Select a status</option>
                          <option key="Active" value="Active">
                            Active
                          </option>
                          <option key="Expired" value="Expired">
                            Expired
                          </option>
                          <option key="Pending" value="Pending">
                            Pending
                          </option>
                          <option key="Pending Policy" value="Pending Policy">
                            Pending Policy
                          </option>
                          <option
                            key="Upcoming Renewals"
                            value="Upcoming Renewals"
                          >
                            Upcoming Renewals
                          </option>
                        </Field>
                        <ErrorMessage
                          name="status"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="cover_note_no" className="form-label">
                        Cover Note No
                      </label>
                      <Field
                        name="cover_note_no"
                        type="text"
                        className={`form-control ${
                          touched.cover_note_no && errors.cover_note_no
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="cover_note_no"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    {!isInsuranceTypeFive && (
                      <>
                        {/* Cover Note No */}
                        {/* <div className="col-md-6 mb-3">
                          <label htmlFor="cover_note_no" className="form-label">
                            Cover Note No
                          </label>
                          <Field
                            name="cover_note_no"
                            type="text"
                            className={`form-control ${
                              touched.cover_note_no && errors.cover_note_no
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <ErrorMessage
                            name="cover_note_no"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div> */}
                        {/* Policy Start Date */}
                        <div className="col-md-6 mb-3">
                          <label
                            htmlFor="policy_start_date"
                            className="form-label"
                          >
                            <span className="text-danger">*</span> Policy Start
                            Date
                          </label>
                          <Field
                            name="policy_start_date"
                            type="date"
                            className={`form-control ${
                              touched.policy_start_date &&
                              errors.policy_start_date
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <ErrorMessage
                            name="policy_start_date"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                        {/* Policy End Date */}
                        <div className="col-md-6 mb-3">
                          <label
                            htmlFor="policy_end_date"
                            className="form-label"
                          >
                            <span className="text-danger">*</span> Policy End
                            Date
                          </label>
                          <Field
                            name="policy_end_date"
                            type="date"
                            className={`form-control ${
                              touched.policy_end_date && errors.policy_end_date
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <ErrorMessage
                            name="policy_end_date"
                            component="div"
                            className="invalid-feedback"
                          />
                        </div>
                      </>
                    )}
                    {/* Supporting Docs */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="supporting_docs" className="form-label">
                        Upload Policy Documents (PDF)
                      </label>
                      <input
                        id="supporting_docs"
                        name="supporting_docs"
                        type="file"
                        multiple
                        className={`form-control ${
                          touched.supporting_docs && errors.supporting_docs
                            ? "is-invalid"
                            : ""
                        }`}
                        accept="application/pdf"
                        onChange={(event) => {
                          const files = Array.from(event.currentTarget.files);
                          setFieldValue("supporting_docs", files);
                        }}
                      />
                      <ErrorMessage
                        name="supporting_docs"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-8 mb-3">
                      <label className="form-label">
                        Existing Policy Documents:
                      </label>
                      {policyDocs && policyDocs.length > 0 ? (
                        policyDocs.map((doc) => (
                          <div key={doc.id}>
                            <a
                              href={doc.document}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {doc.document.split("/").pop()}
                            </a>
                          </div>
                        ))
                      ) : (
                        <p>
                          <b>N/A</b>
                        </p>
                      )}
                    </div>
                    {/* Policy Docs */}
                  </div>
                  <div className=" d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn me-3 ansa-button-secondary"
                      onClick={handleBackClick}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="btn ansa-button-primary"
                      disabled={isSubmitting}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default EditPolicy;
